import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { API, PageModel, Project, ProjectAPI } from "@brg/api";
import { FlexBox, useIsMobile } from "@brg/ui";
import { bProfile } from "@brg/auth";
import { useProfile } from "core/auth";
import { ProjectsGridNew } from "shared/components/grids";
import ProjectCardNew from "shared/components/projectCard/projectCardNew";
import { ItemType } from "shared/constants/itemType";
import { computeProject } from "shared/helpers/types/computed/ComputedProject";
import { CurrentCompetitionSelector } from "shared/reducers/CurrentCompetitionSlice";
import { Pagination } from "shared/components/pagination";
import { TAB_IDS } from "shared/pages/opencallsPage/GetCompetitionItem/const";
import { ProfileRole } from "@brg/auth";
import { HsePolicies } from "shared/constants/HsePolicies";
import { ListOuter } from "../ShortListPage/styles";
import {
  COMPETITION_LONGLIST_CHANGED_EVENT,
  COMPETITION_LONGLIST_GET_LIST_PARAM,
  isCompetitionLonglistTabVisibleByStatus,
} from "_competition/const";

const LONGLIST_PAGE_SIZE = 20;

export const LongListPage: FC<{
  openLikeTab?: boolean;
  externalId?: string;
}> = () => {
  const { i18n } = useTranslation();
  const isMobile = useIsMobile();
  const profile = useProfile();
  const { competition } = useSelector(CurrentCompetitionSelector);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  const isModer = useMemo(() => {
    if (!profile) {
      return false;
    }

    const hasPolicyCompetitionModer = profile.rolesList
      && Array.isArray(profile.rolesList)
      && new ProfileRole(HsePolicies.access_competitions).containsIn(profile.rolesList);

    return bProfile.isModer(profile) || hasPolicyCompetitionModer;
  }, [profile]);

  const isCompetitionModer = useMemo(
    () => !!(profile && competition?.moderatorID?.includes(profile?.designerId)),
    [competition, profile]
  );

  const isCompetitionAdministrator = useMemo(
    () => !!(profile && competition?.administratorID?.includes(profile?.accountId)),
    [competition, profile]
  );

  const isJury = useMemo(
    () => !!(profile && competition?.juryID?.includes(profile?.designerId)),
    [competition, profile]
  );

  const isAdmin = isModer || isCompetitionModer || isCompetitionAdministrator || isJury;

  const showLongListTabForPublic = useMemo(() => {
    const tabEnabled = !!competition?.infoTabs?.find((tab) => tab.tabId === TAB_IDS.longlist)?.enable;
    const nominationFlag = !!(competition?.nominations?.[0] as { showLongList?: boolean } | undefined)?.showLongList;

    return tabEnabled || nominationFlag;
  }, [competition]);

  const canViewLongList = isCompetitionLonglistTabVisibleByStatus(competition?.status)
    && (isAdmin || showLongListTabForPublic);

  const allowedProjectStatus = useMemo(
    () => (competition?.acceptPrivatePublished ? [1, 10] : [1]),
    [competition?.acceptPrivatePublished]
  );

  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    setPage(1);
  }, [competition?.competitionId]);

  useEffect(() => {
    const onLonglistChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ competitionId?: string }>).detail;
      if (detail?.competitionId && detail.competitionId !== competition?.competitionId) {
        return;
      }
      setReloadToken(Date.now());
    };

    window.addEventListener(COMPETITION_LONGLIST_CHANGED_EVENT, onLonglistChanged);
    return () => window.removeEventListener(COMPETITION_LONGLIST_CHANGED_EVENT, onLonglistChanged);
  }, [competition?.competitionId]);

  useEffect(() => {
    if (!competition?.competitionId || !canViewLongList) {
      setProjects([]);
      setPages(1);
      return;
    }

    setIsLoading(true);
    ProjectAPI.GetList({
      isContextIgnored: true,
      competitionIds: [competition.competitionId],
      projectFilter: 1,
      projectStatus: allowedProjectStatus,
      limit: LONGLIST_PAGE_SIZE,
      page,
      [COMPETITION_LONGLIST_GET_LIST_PARAM]: true,
    } as Parameters<typeof ProjectAPI.GetList>[0])
      .then((response) => response.json() as Promise<PageModel<Project>>)
      .then((response) => {
        const data = API.toLang(response.data ?? [], i18n.language) as Project[];
        setProjects(data);
        setPages(Math.max(response.paging?.pages ?? 1, 1));
      })
      .catch(() => {
        setProjects([]);
        setPages(1);
      })
      .finally(() => setIsLoading(false));
  }, [competition?.competitionId, canViewLongList, page, i18n.language, allowedProjectStatus, reloadToken]);

  if (!canViewLongList || !competition) {
    return null;
  }

  return (
    <ListOuter style={{ marginTop: isMobile ? 0 : "24px", padding: 0 }}>
      <FlexBox direction="column" directionMobile="column" id="longlist-scroll-target">
        <ProjectsGridNew k={20} isLoading={isLoading} itemType={ItemType.book}>
          {projects.map((project) => (
            <ProjectCardNew
              key={project.projectId}
              project={project}
              itemType={ItemType.book}
              isModal={false}
              withoutGoToProject={false}
              {...computeProject(project)}
              isSelectable={false}
            />
          ))}
        </ProjectsGridNew>
        <Pagination
          isLoading={isLoading}
          page={page}
          pages={pages}
          onPageChange={(nextPage) => setPage(nextPage)}
          scrollTarget="#longlist-scroll-target"
          hidden={pages <= 1}
        />
      </FlexBox>
    </ListOuter>
  );
};
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { API, PageModel, Project, ProjectAPI } from "@brg/api";
import { FlexBox, useIsMobile } from "@brg/ui";
import { bProfile } from "@brg/auth";
import { useProfile } from "core/auth";
import { ProjectsGridNew } from "shared/components/grids";
import ProjectCardNew from "shared/components/projectCard/projectCardNew";
import { ItemType } from "shared/constants/itemType";
import { computeProject } from "shared/helpers/types/computed/ComputedProject";
import { CurrentCompetitionSelector } from "shared/reducers/CurrentCompetitionSlice";
import { Pagination } from "shared/components/pagination";
import { TAB_IDS } from "shared/pages/opencallsPage/GetCompetitionItem/const";
import { ProfileRole } from "@brg/auth";
import { HsePolicies } from "shared/constants/HsePolicies";
import { ListOuter } from "../ShortListPage/styles";
import {
  COMPETITION_LONGLIST_CHANGED_EVENT,
  COMPETITION_LONGLIST_GET_LIST_PARAM,
  isCompetitionLonglistTabVisibleByStatus,
} from "_competition/const";

const LONGLIST_PAGE_SIZE = 20;

export const LongListPage: FC<{
  openLikeTab?: boolean;
  externalId?: string;
}> = () => {
  const { i18n } = useTranslation();
  const isMobile = useIsMobile();
  const profile = useProfile();
  const { competition } = useSelector(CurrentCompetitionSelector);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  const isModer = useMemo(() => {
    if (!profile) {
      return false;
    }

    const hasPolicyCompetitionModer = profile.rolesList
      && Array.isArray(profile.rolesList)
      && new ProfileRole(HsePolicies.access_competitions).containsIn(profile.rolesList);

    return bProfile.isModer(profile) || hasPolicyCompetitionModer;
  }, [profile]);

  const isCompetitionModer = useMemo(
    () => !!(profile && competition?.moderatorID?.includes(profile?.designerId)),
    [competition, profile]
  );

  const isCompetitionAdministrator = useMemo(
    () => !!(profile && competition?.administratorID?.includes(profile?.accountId)),
    [competition, profile]
  );

  const isJury = useMemo(
    () => !!(profile && competition?.juryID?.includes(profile?.designerId)),
    [competition, profile]
  );

  const isAdmin = isModer || isCompetitionModer || isCompetitionAdministrator || isJury;

  const showLongListTabForPublic = useMemo(() => {
    const tabEnabled = !!competition?.infoTabs?.find((tab) => tab.tabId === TAB_IDS.longlist)?.enable;
    const nominationFlag = !!(competition?.nominations?.[0] as { showLongList?: boolean } | undefined)?.showLongList;

    return tabEnabled || nominationFlag;
  }, [competition]);

  const canViewLongList = isCompetitionLonglistTabVisibleByStatus(competition?.status)
    && (isAdmin || showLongListTabForPublic);

  const allowedProjectStatus = useMemo(
    () => (competition?.acceptPrivatePublished ? [1, 10] : [1]),
    [competition?.acceptPrivatePublished]
  );

  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    setPage(1);
  }, [competition?.competitionId]);

  useEffect(() => {
    const onLonglistChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ competitionId?: string }>).detail;
      if (detail?.competitionId && detail.competitionId !== competition?.competitionId) {
        return;
      }
      setReloadToken(Date.now());
    };

    window.addEventListener(COMPETITION_LONGLIST_CHANGED_EVENT, onLonglistChanged);
    return () => window.removeEventListener(COMPETITION_LONGLIST_CHANGED_EVENT, onLonglistChanged);
  }, [competition?.competitionId]);

  useEffect(() => {
    if (!competition?.competitionId || !canViewLongList) {
      setProjects([]);
      setPages(1);
      return;
    }

    setIsLoading(true);
    ProjectAPI.GetList({
      isContextIgnored: true,
      competitionIds: [competition.competitionId],
      projectFilter: 1,
      projectStatus: allowedProjectStatus,
      limit: LONGLIST_PAGE_SIZE,
      page,
      [COMPETITION_LONGLIST_GET_LIST_PARAM]: true,
    } as Parameters<typeof ProjectAPI.GetList>[0])
      .then((response) => response.json() as Promise<PageModel<Project>>)
      .then((response) => {
        const data = API.toLang(response.data ?? [], i18n.language) as Project[];
        setProjects(data);
        setPages(Math.max(response.paging?.pages ?? 1, 1));
      })
      .catch(() => {
        setProjects([]);
        setPages(1);
      })
      .finally(() => setIsLoading(false));
  }, [competition?.competitionId, canViewLongList, page, i18n.language, allowedProjectStatus, reloadToken]);

  if (!canViewLongList || !competition) {
    return null;
  }

  return (
    <ListOuter style={{ marginTop: isMobile ? 0 : "24px", padding: 0 }}>
      <FlexBox direction="column" directionMobile="column" id="longlist-scroll-target">
        <ProjectsGridNew k={20} isLoading={isLoading} itemType={ItemType.book}>
          {projects.map((project) => (
            <ProjectCardNew
              key={project.projectId}
              project={project}
              itemType={ItemType.book}
              isModal={false}
              withoutGoToProject={false}
              {...computeProject(project)}
              isSelectable={false}
            />
          ))}
        </ProjectsGridNew>
        <Pagination
          isLoading={isLoading}
          page={page}
          pages={pages}
          onPageChange={(nextPage) => setPage(nextPage)}
          scrollTarget="#longlist-scroll-target"
          hidden={pages <= 1}
        />
      </FlexBox>
    </ListOuter>
  );
};
